/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

export const INVITE_TOKEN_TTL_MS = 14 * 24 * 60 * 60 * 1000;
export const INVITE_LINK_BASE = "https://myambits.com/invite";
export const INVITE_PATH_REGEX = /\/invite\/([A-Za-z0-9_-]+)/;

export interface InviterProfile {
  username?: string | null;
  displayName?: string | null;
  photoURL?: string | null;
}

export interface InviteTokenRecord {
  fromUid: string;
  /**
   * The inviter's @handle, or null when they never claimed one (the norm for
   * phone-onboarded accounts). Purely cosmetic — consumers must guard the
   * render rather than assume a string.
   */
  fromUsername: string | null;
  fromDisplayName: string;
  fromPhotoURL: string | null;
  createdAt: number;
  expiresAt: number;
  used: boolean;
  usedBy: string | null;
  label: string | null;
}

export interface BuildInviteArgs {
  uid: string;
  profile: InviterProfile | null | undefined;
  label?: unknown;
  nowMs?: number;
}

export type BuildInviteResult =
  | { valid: true; record: InviteTokenRecord }
  | { valid: false; status: number; error: string };

export function buildInviteRecord(args: BuildInviteArgs): BuildInviteResult {
  const { uid, profile, label, nowMs = Date.now() } = args;

  if (!uid || typeof uid !== "string") {
    return { valid: false, status: 400, error: "Missing uid" };
  }

  // Username is optional. Identity in this app is the phone number — the
  // onboarding flow (ProfileSetupScreen) deliberately never sets a username,
  // so `profile.username` is undefined for every account created since that
  // migration. Rejecting here with a 409 therefore broke invite generation for
  // ALL new users: share link, QR, and contact invite alike.
  //
  // The token itself is an opaque random nanoid and never encodes the
  // username; `fromUsername` is only rendered as a cosmetic "@handle" line
  // beneath the display name on the landing/confirm screens. So a missing
  // username has no functional consequence and must not block the invite.
  const username = profile?.username?.trim() || null;

  // Prefer the real display name; fall back to the handle when that's all we
  // have (legacy accounts), then to a generic label so the record is always
  // renderable.
  const displayName =
    typeof profile?.displayName === "string" && profile.displayName.trim()
      ? profile.displayName.trim()
      : username || "A MyAmbits user";

  const photoURL =
    typeof profile?.photoURL === "string" && profile.photoURL.trim()
      ? profile.photoURL.trim()
      : null;

  let cleanLabel: string | null = null;
  if (typeof label === "string") {
    const trimmed = label.trim();
    if (trimmed.length > 80) {
      return {
        valid: false,
        status: 400,
        error: "label must be 80 chars or fewer",
      };
    }
    cleanLabel = trimmed.length === 0 ? null : trimmed;
  } else if (label !== undefined && label !== null) {
    return { valid: false, status: 400, error: "label must be a string" };
  }

  return {
    valid: true,
    record: {
      fromUid: uid,
      fromUsername: username,
      fromDisplayName: displayName,
      fromPhotoURL: photoURL,
      createdAt: nowMs,
      expiresAt: nowMs + INVITE_TOKEN_TTL_MS,
      used: false,
      usedBy: null,
      label: cleanLabel,
    },
  };
}

export function buildInviteUrl(token: string): string {
  return `${INVITE_LINK_BASE}/${token}`;
}

/** Extracts an invite token from a URL/path. Returns null if no match. */
export function parseInviteToken(url: string | null | undefined): string | null {
  if (!url) return null;
  const m = url.match(INVITE_PATH_REGEX);
  return m ? m[1] : null;
}

export function isInviteExpired(
  record: Pick<InviteTokenRecord, "expiresAt">,
  nowMs: number = Date.now(),
): boolean {
  return record.expiresAt <= nowMs;
}

// localStorage key used by the web "Accept in Browser" flow. InviteLanding
// stashes the token here before signing out the anonymous reader session and
// sending the visitor to AuthScreen; useDeepLinks drains the key into
// PendingInviteContext as soon as a real signed-in user is present, so
// AddFriendConfirmSheet can pick it up just like on the iOS app path.
export const PENDING_INVITE_LS_KEY = "mb_pending_invite_token";
